/**
 * Typed fetch wrapper for the /api/v1 JSON API.
 *  - cookie session (credentials: 'include')
 *  - mandatory CSRF header on every non-GET request
 *  - `{ data }` envelope unwrapping, list pages kept intact
 *  - stable `ApiError` with code / status / details / requestId
 *  - 401 notifies subscribers so the session can be invalidated centrally
 */
import type { ErrorCode } from '@cop/shared';

/** API origin when it is served from its own domain (build-time VITE_API_URL); empty = same origin (dev proxy). */
export const API_ORIGIN = String(import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
export const API_BASE = `${API_ORIGIN}/api/v1`;

/** Absolute URL for an API path such as a signed `/api/v1/files/...` link returned by the server. */
export function apiUrl(path: string): string {
  return path.startsWith('/') ? `${API_ORIGIN}${path}` : path;
}
export const CSRF_HEADER = 'x-cop-csrf';

export interface FieldIssue {
  path: string;
  message: string;
}

export interface Page<T> {
  data: T[];
  page: { nextCursor: string | null; limit: number };
}

export type ApiErrorCode = ErrorCode | 'NETWORK_ERROR' | 'UNKNOWN';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | null;

  constructor(init: { code: ApiErrorCode; status: number; message: string; details?: unknown; requestId?: string | null }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.requestId = init.requestId ?? null;
  }

  /** Field-level issues for VALIDATION_FAILED (empty otherwise). */
  get fieldIssues(): FieldIssue[] {
    if (!Array.isArray(this.details)) return [];
    return this.details
      .filter((d): d is { path: unknown; message: unknown } => !!d && typeof d === 'object' && 'message' in d)
      .map((d) => ({ path: String(d.path ?? ''), message: String(d.message) }));
  }

  /** Map of field path → first message. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const i of this.fieldIssues) if (i.path && !out[i.path]) out[i.path] = i.message;
    return out;
  }

  get isConflict() {
    return this.status === 409;
  }
  get isClientError() {
    return this.status >= 400 && this.status < 500;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Turns any HTTP response body into an ApiError. Exported for tests. */
export function parseApiError(status: number, body: unknown, headerRequestId?: string | null): ApiError {
  const err = (body as { error?: { code?: string; message?: string; details?: unknown; requestId?: string } } | null)?.error;
  if (err && typeof err === 'object' && typeof err.code === 'string') {
    return new ApiError({
      code: err.code as ApiErrorCode,
      status,
      message: err.message || defaultMessage(status),
      details: err.details,
      requestId: err.requestId ?? headerRequestId ?? null,
    });
  }
  return new ApiError({ code: codeForStatus(status), status, message: defaultMessage(status), requestId: headerRequestId ?? null });
}

function codeForStatus(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'BAD_REQUEST';
    case 401:
      return 'UNAUTHENTICATED';
    case 402:
      return 'SUBSCRIPTION_RESTRICTED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 413:
      return 'PAYLOAD_TOO_LARGE';
    case 422:
      return 'VALIDATION_FAILED';
    case 429:
      return 'RATE_LIMITED';
    case 503:
      return 'SERVICE_UNAVAILABLE';
    default:
      return status >= 500 ? 'INTERNAL' : 'UNKNOWN';
  }
}

function defaultMessage(status: number): string {
  if (status === 401) return 'Sign in to continue.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'We could not find what you were looking for.';
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (status >= 500) return 'Something went wrong on our side. Please try again.';
  return 'The request could not be completed.';
}

/* ------------------------------------------------------------ unauthorized hook */

type Listener = (err: ApiError) => void;
const unauthorizedListeners = new Set<Listener>();
export function onUnauthorized(fn: Listener) {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

/* ------------------------------------------------------------ idempotency */

/** Generates a key compatible with the API pattern /^[A-Za-z0-9_\-:.]{8,128}$/. */
export function newIdempotencyKey(prefix = 'web'): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${rand}`;
}

/* ------------------------------------------------------------ core request */

export type Query = Record<string, string | number | boolean | null | undefined | Array<string | number>>;

export function buildQuery(q?: Query): string {
  if (!q) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => sp.append(k, String(x)));
    else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Query;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Skip global 401 handling (used by the session bootstrap and auth forms). */
  silent401?: boolean;
}

let expectedCompany: string | null = null;
/** A precondition, never an authority to select a tenant. */
export function setExpectedCompany(tenantId: string | null) { expectedCompany = tenantId; }

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (expectedCompany && !path.startsWith('/auth/')) headers['x-cop-tenant'] = expectedCompany;
  if (method !== 'GET') headers[CSRF_HEADER] = '1';
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${buildQuery(opts.query)}`, {
      method,
      headers,
      credentials: 'include',
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as { name?: string }).name === 'AbortError') throw e;
    throw new ApiError({ code: 'NETWORK_ERROR', status: 0, message: 'Could not reach the server. Check your connection and try again.' });
  }

  const requestId = res.headers.get('x-request-id');
  const text = res.status === 204 ? '' : await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const err = parseApiError(res.status, body, requestId);
    if (err.code === 'CONFLICT' && (err.details as { reason?: string } | null)?.reason === 'company_context_changed') window.location.replace('/dashboard');
    if (res.status === 401 && !opts.silent401) unauthorizedListeners.forEach((fn) => fn(err));
    throw err;
  }
  return body as T;
}

/** Unwraps `{ data }`. */
async function item<T>(path: string, opts?: RequestOptions): Promise<T> {
  const body = await request<{ data: T }>(path, opts);
  return body?.data as T;
}

export const api = {
  get: <T>(path: string, query?: Query, opts?: Omit<RequestOptions, 'method' | 'query'>) => item<T>(path, { ...opts, query }),
  /** Cursor-paginated list — keeps the `page` block. */
  list: <T>(path: string, query?: Query, opts?: Omit<RequestOptions, 'method' | 'query'>) => request<Page<T>>(path, { ...opts, query }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => item<T>(path, { ...opts, method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => item<T>(path, { ...opts, method: 'PATCH', body: body ?? {} }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => item<T>(path, { ...opts, method: 'PUT', body: body ?? {} }),
  raw: request,
};

/** Human copy for error codes that deserve a specific message regardless of server text. */
export function describeError(e: unknown): { title: string; message: string } {
  if (!isApiError(e)) return { title: 'Something went wrong', message: e instanceof Error ? e.message : 'Please try again.' };
  switch (e.code) {
    case 'NETWORK_ERROR':
      return { title: 'You appear to be offline', message: e.message };
    case 'FORBIDDEN':
      return { title: 'Not allowed', message: e.message };
    case 'NOT_FOUND':
      return { title: 'Not found', message: e.message };
    case 'RATE_LIMITED':
      return { title: 'Slow down', message: e.message };
    case 'ENTITLEMENT_EXCEEDED':
      return { title: 'Plan limit reached', message: e.message };
    case 'SUBSCRIPTION_RESTRICTED':
      return { title: 'Subscription restricted', message: e.message };
    case 'CONFLICT':
    case 'REVISION_CONFLICT':
      return { title: 'This changed while you were working', message: e.message };
    case 'INVALID_STATE_TRANSITION':
      return { title: 'Not possible right now', message: e.message };
    case 'VALIDATION_FAILED':
      return { title: 'Check the highlighted fields', message: e.message };
    default:
      return { title: e.status >= 500 ? 'Server error' : 'Request failed', message: e.message };
  }
}
